import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

interface DocumentGeneratorModalProps {
  onboardingId: string;
  clientName: string;
  onClose: () => void;
}

interface GeneratedDocument {
  documentId: string;
  documentType: string;
  documentName: string;
  status: string;
  contentHtml: string;
  contentMarkdown: string;
  createdAt: string;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1e293b;
  border-radius: 16px;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const AIBadge = styled.span`
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.5rem;
  cursor: pointer;
  &:hover { color: #ffffff; }
`;

const ModalBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 280px;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  overflow-y: auto;
`;

const SidebarSection = styled.div`
  margin-bottom: 2rem;
`;

const SidebarTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 1rem 0;
`;

const DocumentButton = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem 1rem;
  background: ${props => props.active ? 'rgba(59, 130, 246, 0.2)' : 'transparent'};
  border: 1px solid ${props => props.active ? 'rgba(59, 130, 246, 0.3)' : 'transparent'};
  border-radius: 8px;
  color: ${props => props.active ? '#ffffff' : '#94a3b8'};
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #ffffff;
  }
`;

const DocIcon = styled.span`
  font-size: 1.25rem;
`;

const GenerateButton = styled.button<{ generating?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
  background: ${props => props.generating ? 'rgba(139, 92, 246, 0.2)' : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'};
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: ${props => props.generating ? 'wait' : 'pointer'};
  transition: all 0.2s;
  margin-bottom: 0.5rem;

  &:hover {
    opacity: ${props => props.generating ? 1 : 0.9};
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  font-size: 0.65rem;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  text-transform: uppercase;
  font-weight: 600;

  ${props => {
    switch (props.status) {
      case 'DRAFT': return 'background: rgba(234, 179, 8, 0.2); color: #eab308;';
      case 'APPROVED': return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
      case 'SENT_TO_CLIENT': return 'background: rgba(59, 130, 246, 0.2); color: #3b82f6;';
      case 'SIGNED': return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
      default: return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 1.5rem 2rem;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.2);
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #64748b;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const DocumentPreview = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 2rem;
  color: #1e293b;
  font-family: 'Georgia', serif;
  line-height: 1.8;
  min-height: 100%;

  h1 { font-size: 1.5rem; color: #0f172a; margin-bottom: 1rem; border-bottom: 2px solid #e2e8f0; padding-bottom: 0.5rem; }
  h2 { font-size: 1.25rem; color: #1e293b; margin-top: 1.5rem; margin-bottom: 0.75rem; }
  h3 { font-size: 1.1rem; color: #334155; margin-top: 1rem; margin-bottom: 0.5rem; }
  p { margin-bottom: 1rem; }
  strong { font-weight: 600; }
  hr { border: none; border-top: 1px solid #e2e8f0; margin: 1.5rem 0; }
  table { width: 100%; border-collapse: collapse; margin: 1rem 0; }
  th, td { border: 1px solid #e2e8f0; padding: 0.5rem; text-align: left; }
  th { background: #f8fafc; }
  li { margin-bottom: 0.5rem; }
`;

const ActionBar = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: #0f172a;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'success' }>`
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white; border: none;`;
      case 'success':
        return `background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%); color: white; border: none;`;
      default:
        return `background: rgba(255, 255, 255, 0.1); color: #e2e8f0; border: 1px solid rgba(255, 255, 255, 0.2);`;
    }
  }}

  &:hover { opacity: 0.9; }
`;

const DOCUMENT_TYPES = [
  { type: 'INVESTMENT_POLICY_STATEMENT', label: 'Investment Policy Statement', icon: '📋', endpoint: 'ips' },
  { type: 'ADVISORY_AGREEMENT', label: 'Advisory Agreement', icon: '📝', endpoint: 'advisory-agreement' },
  { type: 'WELCOME_LETTER', label: 'Welcome Letter', icon: '✉️', endpoint: 'welcome-letter' },
];

const DocumentGeneratorModal: React.FC<DocumentGeneratorModalProps> = ({
  onboardingId,
  clientName,
  onClose
}) => {
  const [documents, setDocuments] = useState<GeneratedDocument[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<GeneratedDocument | null>(null);
  const [generating, setGenerating] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocuments();
  }, [onboardingId]);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`/api/onboarding-ai/${onboardingId}/documents`);
      setDocuments(response.data);
      if (response.data.length > 0) {
        setSelectedDoc(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (docType: typeof DOCUMENT_TYPES[0]) => {
    setGenerating(docType.type);
    try {
      const response = await axios.post(`/api/onboarding-ai/${onboardingId}/documents/${docType.endpoint}`);
      const newDoc = response.data;
      setDocuments([newDoc, ...documents.filter(d => d.documentType !== docType.type)]);
      setSelectedDoc(newDoc);
    } catch (error) {
      console.error('Error generating document:', error);
      window.alert('Failed to generate document. Please try again.');
    } finally {
      setGenerating(null);
    }
  };

  const handleApprove = async () => {
    if (!selectedDoc) return;
    try {
      const response = await axios.put(`/api/onboarding-ai/documents/${selectedDoc.documentId}/status`, {
        status: 'APPROVED',
        reviewNotes: 'Approved via document generator'
      });
      setSelectedDoc(response.data);
      setDocuments(documents.map(d => d.documentId === selectedDoc.documentId ? response.data : d));
    } catch (error) {
      console.error('Error approving document:', error);
    }
  };

  const handleEnhanceWithAI = async () => {
    if (!selectedDoc) return;
    try {
      const response = await axios.post(`/api/onboarding-ai/documents/${selectedDoc.documentId}/enhance`);
      setSelectedDoc(response.data);
      setDocuments(documents.map(d => d.documentId === selectedDoc.documentId ? response.data : d));
    } catch (error) {
      console.error('Error enhancing document:', error);
    }
  };

  const getDocumentForType = (type: string) => {
    return documents.find(d => d.documentType === type);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            Document Generator
            <AIBadge>AI Powered</AIBadge>
          </ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>
          <Sidebar>
            <SidebarSection>
              <SidebarTitle>Generate Documents</SidebarTitle>
              {DOCUMENT_TYPES.map(docType => {
                const existingDoc = getDocumentForType(docType.type);
                return (
                  <div key={docType.type}>
                    <GenerateButton
                      onClick={() => handleGenerate(docType)}
                      generating={generating === docType.type}
                      disabled={generating !== null}
                    >
                      {generating === docType.type ? (
                        <>Generating...</>
                      ) : (
                        <>
                          <span>{docType.icon}</span>
                          {existingDoc ? 'Regenerate' : 'Generate'} {docType.label}
                        </>
                      )}
                    </GenerateButton>
                  </div>
                );
              })}
            </SidebarSection>

            <SidebarSection>
              <SidebarTitle>Generated Documents</SidebarTitle>
              {documents.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
                  No documents generated yet
                </div>
              ) : (
                documents.map(doc => (
                  <DocumentButton
                    key={doc.documentId}
                    active={selectedDoc?.documentId === doc.documentId}
                    onClick={() => setSelectedDoc(doc)}
                  >
                    <DocIcon>
                      {DOCUMENT_TYPES.find(t => t.type === doc.documentType)?.icon || '📄'}
                    </DocIcon>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                        {doc.documentName.split(' - ')[0]}
                      </div>
                      <StatusBadge status={doc.status}>{doc.status}</StatusBadge>
                    </div>
                  </DocumentButton>
                ))
              )}
            </SidebarSection>
          </Sidebar>

          <ContentArea>
            {!selectedDoc ? (
              <EmptyState>
                <EmptyIcon>📄</EmptyIcon>
                <h3 style={{ color: '#94a3b8', marginBottom: '0.5rem' }}>No Document Selected</h3>
                <p>Generate a document using the buttons on the left,<br />or select an existing document to preview.</p>
              </EmptyState>
            ) : (
              <DocumentPreview dangerouslySetInnerHTML={{ __html: selectedDoc.contentHtml }} />
            )}
          </ContentArea>
        </ModalBody>

        {selectedDoc && (
          <ActionBar>
            <ActionButton onClick={handleEnhanceWithAI}>
              ✨ Enhance with AI
            </ActionButton>
            <ActionButton onClick={() => {
              const blob = new Blob([selectedDoc.contentMarkdown], { type: 'text/markdown' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${selectedDoc.documentName}.md`;
              a.click();
            }}>
              📥 Download Markdown
            </ActionButton>
            <div style={{ flex: 1 }} />
            {selectedDoc.status === 'DRAFT' && (
              <ActionButton variant="success" onClick={handleApprove}>
                ✓ Approve Document
              </ActionButton>
            )}
          </ActionBar>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default DocumentGeneratorModal;
